import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, Minus, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useCart } from '../contexts/CartContext';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { formatPrice, calculateDiscount } from '../lib/utils';

export const Cart = () => {
  const { t } = useTranslation();
  const { cart, removeFromCart, updateQuantity, getCartTotal } = useCart();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-12 h-12 text-muted-foreground" />
          </div>
          <h2 className="font-serif text-3xl font-bold mb-4">{t('cart.empty')}</h2>
          <p className="text-muted-foreground mb-8">{t('cart.emptyMessage')}</p>
          <Link to="/shop">
            <Button size="lg">{t('cart.continueShopping')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const shippingFee = Number(settings?.shipping_fee) || 0;
  const freeShippingThreshold = Number(settings?.free_shipping_threshold) || 0;
  const cartSubtotal = Number(getCartTotal()) || 0;
  const qualifiesFreeShipping = freeShippingThreshold > 0 && cartSubtotal >= freeShippingThreshold;

  const shippingLabel = (qualifiesFreeShipping || shippingFee <= 0)
    ? t('cart.free')
    : formatPrice(shippingFee);

  return (
    <div className="min-h-screen py-8">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-foreground mb-8">
            {t('cart.title')}
          </h1>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item, index) => {
                const finalPrice = item.discount > 0
                  ? calculateDiscount(item.price, item.discount)
                  : item.price;

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="p-4">
                      <div className="flex gap-4">
                        <Link to={`/product/${item.id}`} className="flex-shrink-0">
                          <div className="w-24 h-24 rounded-lg overflow-hidden bg-muted">
                            {item.images && item.images[0] ? (
                              <img
                                src={item.images[0]}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <span className="text-2xl text-muted-foreground">NC</span>
                              </div>
                            )}
                          </div>
                        </Link>

                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between gap-4 mb-2">
                            <div>
                              <Link to={`/product/${item.id}`}>
                                <h3 className="font-semibold text-lg hover:text-primary transition-smooth line-clamp-1">
                                  {item.name}
                                </h3>
                              </Link>
                              <p className="text-sm text-muted-foreground">{item.brand}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeFromCart(item.id)}
                              className="flex-shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>

                          <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center space-x-3">
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="text-sm font-medium w-8 text-center">
                                {item.quantity}
                              </span>
                              <Button
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>

                            <div className="text-right">
                              <p className="text-lg font-bold text-primary">
                                {formatPrice(finalPrice * item.quantity)}
                              </p>
                              {item.discount > 0 && (
                                <p className="text-xs text-muted-foreground line-through">
                                  {formatPrice(item.price * item.quantity)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="p-6 sticky top-24">
                <h2 className="font-serif text-2xl font-bold mb-6">Order Summary</h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('cart.subtotal')}</span>
                    <span className="font-semibold">{formatPrice(getCartTotal())}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('cart.shipping')}</span>
                    <span className="font-semibold text-success">{shippingLabel}</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold">{t('cart.total')}</span>
                      <span className="text-2xl font-bold text-primary">
                        {formatPrice(getCartTotal() + shippingFee)}
                      </span>
                    </div>
                  </div>
                </div>

                <Button
                  size="lg"
                  className="w-full mb-3"
                  onClick={() => navigate('/checkout')}
                >
                  {t('cart.proceedToCheckout')}
                </Button>
                <Link to="/shop">
                  <Button variant="outline" size="lg" className="w-full">
                    {t('cart.continueShopping')}
                  </Button>
                </Link>

                <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm text-center text-muted-foreground">
                    
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};